import React, { useState, useEffect, useMemo } from "react";
import { Mail, Search, Check, UserPlus, Phone, Home, Trash2, Wand2, Loader2 } from "lucide-react";
import { getAdminEnquiries, updateEnquiryStatus, addLeadManual, deleteEnquiry, dedupeEnquiries } from "../../api";
import { useToast } from "../../hooks/use-toast";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "../ui/select";

const sColor = { new: "bg-blue-100 text-blue-700", handled: "bg-green-100 text-green-700" };
const fmtDate = (d) => (d ? new Date(d).toLocaleDateString("en-GB", { day: "numeric", month: "short", year: "numeric" }) : "");

const AdminEnquiries = () => {
  const { toast } = useToast();
  const [rows, setRows] = useState([]);
  const [q, setQ] = useState("");
  const [filter, setFilter] = useState("all");

  const refresh = () => getAdminEnquiries().then((d) => setRows(d.map((e) => ({ ...e, status: e.status || "new" })))).catch(() => setRows([]));
  useEffect(() => { refresh(); }, []);

  const setStatus = async (id, status) => {
    try { const u = await updateEnquiryStatus(id, status); setRows((rs) => rs.map((r) => r.id === id ? { ...r, ...u, status } : r)); toast({ title: status === "handled" ? "Marked handled" : "Reopened" }); }
    catch { toast({ title: "Update failed" }); }
  };

  const toCrm = async (e) => {
    try {
      await addLeadManual({ name: e.name, email: e.email, phone: e.phone || "", message: e.message || `Enquiry about ${e.propertyTitle || "a property"}`, type: "enquiry" });
      toast({ title: "Added to CRM", description: "New lead created in Leads / CRM." });
    } catch (err) { toast({ title: "Couldn't add to CRM", description: err?.response?.data?.detail || "" }); }
  };

  const remove = async (e) => {
    if (!window.confirm(`Delete the enquiry from ${e.name || "this contact"}? This can't be undone.`)) return;
    try { await deleteEnquiry(e.id); setRows((rs) => rs.filter((r) => r.id !== e.id)); toast({ title: "Enquiry deleted" }); }
    catch { toast({ title: "Couldn't delete enquiry" }); }
  };

  const [dedup, setDedup] = useState(false);
  const dedupe = async () => {
    if (!window.confirm("Remove duplicate enquiries (same person, property & message)? This can't be undone.")) return;
    setDedup(true);
    try { const r = await dedupeEnquiries(); toast({ title: r.removed ? `${r.removed} duplicate${r.removed !== 1 ? "s" : ""} removed` : "No duplicates found", description: `${r.remaining} enquiries remain.` }); refresh(); }
    catch { toast({ title: "Couldn't dedupe" }); }
    finally { setDedup(false); }
  };

  const stats = useMemo(() => ({
    total: rows.length,
    open: rows.filter((r) => r.status !== "handled").length,
    handled: rows.filter((r) => r.status === "handled").length,
  }), [rows]);

  const filtered = useMemo(() => rows.filter((r) => {
    if (filter !== "all" && r.status !== filter) return false;
    if (!q.trim()) return true;
    const s = q.toLowerCase();
    return [r.name, r.email, r.phone, r.message, r.propertyTitle].filter(Boolean).some((f) => f.toLowerCase().includes(s));
  }), [rows, q, filter]);

  return (
    <div className="bg-white rounded-2xl border border-[#f0ece5] p-6" data-testid="admin-enquiries">
      <h3 className="font-display text-[20px] font-500 text-[#2b2b2b] mb-5 flex items-center gap-2"><Mail size={19} className="text-[#b8935f]" /> Enquiries ({rows.length})
        <button onClick={dedupe} disabled={dedup} data-testid="enquiry-dedupe-btn" className="ml-auto inline-flex items-center gap-1.5 px-3 py-1.5 rounded-full bg-white border border-[#e2d8c8] text-[12px] text-[#6a6a6a] hover:border-[#b8935f] hover:text-[#b8935f] transition-colors disabled:opacity-60">{dedup ? <Loader2 size={13} className="animate-spin" /> : <Wand2 size={13} />} Remove duplicates</button>
      </h3>

      <div className="grid grid-cols-3 gap-3 mb-6">
        {[["Total", stats.total], ["To action", stats.open], ["Handled", stats.handled]].map(([l, v]) => (
          <div key={l} className="p-4 rounded-xl bg-[#faf7f2] border border-[#f0ece5]" data-testid="enquiry-stat">
            <p className="text-[11px] uppercase tracking-wide text-[#a0a0a0] mb-1">{l}</p>
            <p className="font-display text-[22px] font-500 text-[#2b2b2b] leading-none">{v}</p>
          </div>
        ))}
      </div>

      <div className="flex flex-wrap gap-2 mb-5">
        <div className="relative flex-1 min-w-[220px]">
          <Search size={16} className="absolute left-3 top-1/2 -translate-y-1/2 text-[#a0a0a0]" />
          <input placeholder="Search enquiries…" value={q} onChange={(e) => setQ(e.target.value)} className="w-full h-[44px] pl-10 pr-3 rounded-lg border border-[#e2d8c8] text-[14px] outline-none focus:ring-2 focus:ring-[#b8935f]/30 focus:border-[#b8935f]" data-testid="enquiry-search" />
        </div>
        <Select value={filter} onValueChange={setFilter}>
          <SelectTrigger className="w-[160px] h-[44px] border-[#e2d8c8]" data-testid="enquiry-filter"><SelectValue /></SelectTrigger>
          <SelectContent>
            <SelectItem value="all">All</SelectItem>
            <SelectItem value="new">New</SelectItem>
            <SelectItem value="handled">Handled</SelectItem>
          </SelectContent>
        </Select>
      </div>

      {filtered.length === 0 ? <p className="p-10 text-center text-[#8a8a8a]">No enquiries match.</p> : (
        <div className="space-y-2">
          {filtered.map((e) => (
            <div key={e.id} className={`p-4 rounded-xl border ${e.status === "handled" ? "bg-white border-[#f0ece5] opacity-75" : "bg-[#faf7f2] border-[#f0ece5]"}`} data-testid="enquiry-row">
              <div className="flex items-start justify-between gap-3">
                <div className="min-w-0">
                  <p className="text-[15px] font-500 text-[#2b2b2b]">{e.name} <span className={`text-[11px] px-2 py-0.5 rounded-full ml-1 capitalize ${sColor[e.status]}`}>{e.status}</span></p>
                  <p className="text-[13px] text-[#8a8a8a] flex flex-wrap items-center gap-x-3"><span className="flex items-center gap-1"><Mail size={12} /> {e.email}</span>{e.phone && <span className="flex items-center gap-1"><Phone size={12} /> {e.phone}</span>}{e.propertyTitle && <span className="flex items-center gap-1"><Home size={12} /> {e.propertyTitle}</span>}</p>
                  {e.message && <p className="text-[13px] text-[#6a6a6a] mt-1.5">{e.message}</p>}
                </div>
                <span className="text-[12px] text-[#a0a0a0] shrink-0">{fmtDate(e.createdAt)}</span>
              </div>
              <div className="flex items-center gap-2 mt-3">
                <button onClick={() => setStatus(e.id, e.status === "handled" ? "new" : "handled")} data-testid="enquiry-handle-btn" className={`inline-flex items-center gap-1.5 px-3 py-2 rounded-lg border text-[12px] transition-colors ${e.status === "handled" ? "border-[#e2d8c8] text-[#6a6a6a] hover:border-[#b8935f] hover:text-[#b8935f]" : "border-green-300 text-green-600 hover:bg-green-50"}`}><Check size={14} /> {e.status === "handled" ? "Reopen" : "Mark handled"}</button>
                <button onClick={() => toCrm(e)} data-testid="enquiry-to-crm-btn" className="inline-flex items-center gap-1.5 px-3 py-2 rounded-lg border border-[#e2d8c8] text-[12px] text-[#6a6a6a] hover:border-[#b8935f] hover:text-[#b8935f] transition-colors"><UserPlus size={14} /> Add to CRM</button>
                <button onClick={() => remove(e)} data-testid="enquiry-delete-btn" className="inline-flex items-center gap-1.5 px-3 py-2 rounded-lg border border-[#e2d8c8] text-[12px] text-[#a0a0a0] hover:border-red-300 hover:text-red-500 transition-colors ml-auto"><Trash2 size={14} /> Delete</button>
              </div>
            </div>
          ))}
        </div>
      )}
    </div>
  );
};

export default AdminEnquiries;
